import { useState } from 'react';
import type { RunState } from '../run/types';
import { getGameDefinition } from '../games/registry';
import { formatDelta, formatDuration } from '../shared/time';
import { getRunTotalMs } from '../run/runReducer';
import { copyText } from '../shared/clipboard';

type Props = { run: RunState; onHome: () => void; onReplay: (seed: string) => void };

export function ResultsScreen({ run, onHome, onReplay }: Props) {
  const total = getRunTotalMs(run);
  const [copyStatus, setCopyStatus] = useState('');
  return (
    <main className="results-shell" data-run-phase="results">
      <section className="results-hero">
        <p className="eyebrow">Gauntle Unlimited archive</p>
        <h2>Run complete</h2>
        <h1>{formatDuration(total)}</h1>
        <p className="results-rank">Hypothetical archive rank: unavailable for unlimited seeds</p>
        <p className="results-note">
          This local result does not affect Gauntle statistics or leaderboards.
        </p>
        <p>
          Seed <code>{run.seed}</code> · {run.mode} mode
        </p>
      </section>
      <div className="results-table-wrap">
        <table className="results-table">
          <thead>
            <tr>
              <th scope="col">Game</th>
              <th scope="col">Time</th>
              <th scope="col">Bonus / penalty</th>
              <th scope="col">Flawless?</th>
            </tr>
          </thead>
          <tbody>
            {run.results.map((result) => (
              <tr key={result.gameId}>
                <th scope="row">{getGameDefinition(result.gameId).displayName}</th>
                <td>{formatDuration(result.rawTimeMs)}</td>
                <td className={result.deltaMs > 0 ? 'positive-delta' : 'negative-delta'}>
                  {formatDelta(result.deltaMs)}
                </td>
                <td className={`outcome-label outcome-label--${result.outcome}`}>
                  {result.outcome === 'solved' && result.deltaMs <= 0 ? 'Yes' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" colSpan={3}>
                Total
              </th>
              <td>{formatDuration(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="button-row results-actions">
        <button className="button button--secondary" onClick={onHome}>
          Home
        </button>
        <button className="button" onClick={() => onReplay(run.seed)}>
          Replay seed
        </button>
        <button
          className="button button--secondary"
          onClick={async () => {
            const copied = await copyText(run.seed);
            setCopyStatus(copied ? 'Seed copied.' : 'Could not copy seed.');
          }}
        >
          Copy seed
        </button>
      </div>
      <p className="copy-status" role="status">
        {copyStatus}
      </p>
    </main>
  );
}
